import { Avatar, type AvatarProps } from '@affine/component';
import {
  type WorkspaceMetadata,
  WorkspacesService,
} from '@affine/core/modules/workspace';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useLayoutEffect, useState } from 'react';

import { AuthService, ServerService } from '../../modules/cloud';
import { AvatarAssetCacheService } from '../../modules/cloud/services/avatar-asset-cache';

const WORKSPACE_LOGO_BITMAP_CACHE_LIMIT = 64;

type WorkspaceAvatarBitmapCacheEntry = {
  imageBitmap: ImageBitmap;
  key: string;
  lastAccessedAt: number;
};

const bitmapCache = new Map<string, WorkspaceAvatarBitmapCacheEntry>();

function bitmapCacheKey(
  serverId: string,
  accountId: string,
  workspaceId: string,
  avatarKey: string
) {
  return `${serverId}:${accountId}:${workspaceId}:${avatarKey}`;
}

function rememberBitmapCache(
  key: string,
  entry: Omit<WorkspaceAvatarBitmapCacheEntry, 'lastAccessedAt'>
) {
  bitmapCache.set(key, {
    ...entry,
    lastAccessedAt: Date.now(),
  });

  if (bitmapCache.size <= WORKSPACE_LOGO_BITMAP_CACHE_LIMIT) {
    return;
  }

  const evicted = Array.from(bitmapCache.entries())
    .toSorted((a, b) => a[1].lastAccessedAt - b[1].lastAccessedAt)
    .slice(0, bitmapCache.size - WORKSPACE_LOGO_BITMAP_CACHE_LIMIT);

  for (const [evictedKey] of evicted) {
    bitmapCache.delete(evictedKey);
  }
}

async function blobToImageBitmap(blob: Blob): Promise<ImageBitmap> {
  const image = document.createElement('img');
  const objectUrl = URL.createObjectURL(blob);
  try {
    image.src = objectUrl;
    await image.decode();
    // limit the size of the image data to reduce memory usage
    const hRatio = 128 / image.naturalWidth;
    const vRatio = 128 / image.naturalHeight;
    const ratio = Math.min(hRatio, vRatio);
    return await createImageBitmap(image, {
      resizeWidth: image.naturalWidth * ratio,
      resizeHeight: image.naturalHeight * ratio,
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * workspace avatar component with automatic cache, and avoid flashing
 */
export const WorkspaceAvatar = ({
  meta,
  ...otherProps
}: { meta: WorkspaceMetadata } & AvatarProps) => {
  const workspacesService = useService(WorkspacesService);
  const avatarCache = useService(AvatarAssetCacheService);
  const authService = useService(AuthService);
  const server = useService(ServerService).server;
  const account = useLiveData(authService.session.account$);

  const profile = workspacesService.getProfile(meta);

  useEffect(() => {
    profile.revalidate();
  }, [meta, profile]);

  const avatarKey = useLiveData(profile.profile$.map(v => v?.avatar));
  const cachedBitmapKey =
    avatarKey && account?.id
      ? bitmapCacheKey(server.id, account.id, meta.id, avatarKey)
      : null;
  const displayKey = avatarKey
    ? (cachedBitmapKey ?? `${meta.id}:${avatarKey}`)
    : null;

  const [downloadedAvatar, setDownloadedAvatar] = useState<
    { imageBitmap: ImageBitmap; key: string } | undefined
  >(cachedBitmapKey ? bitmapCache.get(cachedBitmapKey) : undefined);

  useLayoutEffect(() => {
    if (!avatarKey || !displayKey || !meta) {
      setDownloadedAvatar(undefined);
      return;
    }

    let canceled = false;
    const cacheInput =
      account?.id && avatarKey
        ? {
            kind: 'workspace-logo' as const,
            scope: {
              accountId: account.id,
              serverId: server.id,
            },
            entityId: meta.id,
            source: avatarKey,
          }
        : null;

    const showBitmap = (imageBitmap: ImageBitmap) => {
      if (canceled) {
        return;
      }
      setDownloadedAvatar(prev => {
        if (prev?.key === displayKey) {
          return prev;
        }
        return { imageBitmap, key: displayKey };
      });
      if (cachedBitmapKey) {
        rememberBitmapCache(cachedBitmapKey, {
          imageBitmap,
          key: displayKey,
        });
      }
    };

    void (async () => {
      try {
        if (cachedBitmapKey) {
          const cachedBitmap = bitmapCache.get(cachedBitmapKey);
          if (cachedBitmap) {
            cachedBitmap.lastAccessedAt = Date.now();
            showBitmap(cachedBitmap.imageBitmap);
          }
        }

        if (cacheInput) {
          const cachedRecord = await avatarCache.get(cacheInput);
          if (cachedRecord && !canceled) {
            showBitmap(
              await blobToImageBitmap(
                new Blob([cachedRecord.bytes], { type: cachedRecord.mime })
              )
            );
          }
        }

        const blob = await workspacesService.getWorkspaceBlob(meta, avatarKey);
        if (!blob || canceled) {
          return;
        }

        const cachedRecord = cacheInput
          ? await avatarCache.cacheBlob(cacheInput, blob)
          : null;
        const displayBlob = cachedRecord
          ? new Blob([cachedRecord.bytes], { type: cachedRecord.mime })
          : blob;
        showBitmap(await blobToImageBitmap(displayBlob));
      } catch (err) {
        console.error('get workspace blob error: ' + err);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [
    account?.id,
    avatarCache,
    avatarKey,
    cachedBitmapKey,
    displayKey,
    meta,
    server.id,
    workspacesService,
  ]);

  return <Avatar image={downloadedAvatar?.imageBitmap} {...otherProps} />;
};
